/**
 * useCalculateLines - Hook para calcular líneas de shipment
 * 
 * Permite calcular las líneas de un shipment basándose en peso y número de items.
 * Realiza POST a /invoices/shipments/calculate-lines con order_id, weight y num_items.
 * Muestra toasts de éxito/error.
 * 
 * @returns {Object} { calculateLines: función de cálculo, isCalculating: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export interface CalculateLinesPayload {
  order_id: number;
  weight: number;
  num_items: number;
}

export interface CalculateLinesResponse {
  order_id: number;
  weight_kg: number;
  lines: Array<{
    concept: string;
    base_amount: number;
    total_amount: number;
    taxes: any;
  }>;
  summary: {
    total_base_amount: number;
    total_taxes: number;
    total_amount: number;
  };
}

export const useCalculateLines = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isCalculating, setIsCalculating] = useState(false);

  const calculateLines = useCallback(
    async (
      payload: CalculateLinesPayload
    ): Promise<CalculateLinesResponse | null> => {
      setIsCalculating(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        const response = await shipmentsAdapter.calculateLines(
          payload.order_id,
          payload.weight,
          payload.num_items
        );

        return response;
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.calculate_lines_error.unexpected',
              'Error inesperado al calcular las líneas. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.calculate_lines_error.title', 'Error al calcular'),
          description,
          variant: 'destructive',
        });

        return null;
      } finally {
        setIsCalculating(false);
      }
    },
    [t, toast]
  );

  return {
    calculateLines,
    isCalculating,
  };
};

